import { useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Upload } from 'lucide-react'
import { Button } from 'components/ui/button'
import Spinner from 'components/Spinner'
import Message from 'components/Message'
import ImageGallery from 'components/ImageGallery/ImageGallery'
import ImageReviewPopup from 'components/ImageGallery/ImageReviewPopup'
import LocationImagesEmptyState from 'components/LocationImagesEmptyState'
import ImageUploadWizard, { commitImageUpload, siteImageAdapter } from 'components/ImageUploadWizard'
import { toast } from 'components/ui/toast-store'
import { getSiteImages, updateSiteImage, deleteSiteImage } from 'slices/siteImageSlice'
import type { SiteOutletContext } from '../Sites_ShowCN'

const SiteImages = () => {
	const { site, siteId } = useOutletContext<SiteOutletContext>()
	const dispatch = useDispatch()

	const [wizardOpen, setWizardOpen] = useState(false)
	const [reviewImage, setReviewImage] = useState<any>(null)

	const userInfo = useSelector((state: any) => state.userLogin.userInfo)
	const { entities, ids, isLoading, error } = useSelector((state: any) => state.siteImages)

	useEffect(() => {
		dispatch(getSiteImages(siteId) as any)
	}, [dispatch, siteId])

	const images = useMemo(
		() => ids.map((id: string) => entities[id]).filter(Boolean),
		[ids, entities],
	)

	const uploadTarget = useMemo(
		() => ({
			entityLabel: site.name || 'this site',
			folder: `mdt/sites/${siteId}`,
			onCommit: (commit: any, state: any) =>
				commitImageUpload(
					commit,
					siteImageAdapter({ dispatch, userId: userInfo._id, siteId }),
					{},
					state,
				),
		}),
		[dispatch, userInfo._id, siteId, site.name],
	)

	const handleDelete = async (image: any) => {
		const ok = await toast.confirm({
			title: 'Delete image',
			message: 'Delete this image? This cannot be undone.',
		})
		if (!ok) return
		await dispatch(deleteSiteImage(image._id) as any)
		setReviewImage(null)
	}

	const handleUpdateNote = async (updated: any) => {
		await dispatch(updateSiteImage(updated) as any)
		setReviewImage(null)
	}

	if (isLoading && images.length === 0) return <Spinner containerHeight="50vh" />
	if (error) return <Message variant="danger">{error}</Message>

	const hasImages = images.length > 0

	return (
		<div>
			{hasImages && (
				<div className="mb-6 flex flex-wrap items-center justify-between gap-4">
					<div className="flex flex-col gap-1">
						<h2 className="m-0 text-2xl font-bold">Images</h2>
						<span className="text-[0.95rem] text-muted-foreground">
							Maintain a visual record of this site with organized photo uploads.
						</span>
					</div>
					<Button type="button" variant="primary" onClick={() => setWizardOpen(true)}>
						<span className="flex items-center gap-2">
							<Upload className="h-4 w-4" />
							Upload Images
						</span>
					</Button>
				</div>
			)}

			{hasImages ? (
				<ImageGallery
					locationImages={images}
					handleDeleteImage={handleDelete}
					handlePreviewImage={setReviewImage}
				/>
			) : (
				<LocationImagesEmptyState onUploadClick={() => setWizardOpen(true)} />
			)}

			<ImageReviewPopup
				open={!!reviewImage}
				onClose={() => setReviewImage(null)}
				image={reviewImage}
				onUpdateNote={handleUpdateNote}
				onDeleteImage={handleDelete}
			/>

			{/* Sibling of the review popup, never nested: ui/dialog clears the
			    body scroll lock on unmount, so a nested close would restore
			    page scrolling behind the still-open outer dialog. */}
			<ImageUploadWizard
				open={wizardOpen}
				onClose={() => setWizardOpen(false)}
				target={uploadTarget}
			/>
		</div>
	)
}

export default SiteImages
